import { useMemo } from "react";

import { usePermissions } from "@/hooks/usePermissions";
import { useServerSideFilters } from "@/hooks/useQueryFilters";
import type { InvoiceTypeString } from "@/lib/constants/invoice-type";
import { InvoiceStatus } from "@/lib/constants/invoiceStatus";
import { InvoiceReviewType } from "@/lib/constants/invoices";
import { useTenant } from "@/providers/session";

type InitialQueryProps = {
  type: InvoiceTypeString | null;
};

export const useExportInitialQuery = ({ type }: InitialQueryProps) => {
  const { isSuperAdmin } = usePermissions();
  const { isOcrReviewRequired } = useTenant();

  const query = useMemo(() => {
    const baseQuery = {
      ...(type && { type }),
      status: [
        InvoiceStatus.REVIEWED,
        InvoiceStatus.EXPORTING,
        InvoiceStatus.EXPORT_FAILED,
      ],
    };

    if (isOcrReviewRequired && !isSuperAdmin) {
      return {
        ...baseQuery,
        ocrReviewed: true,
      };
    }

    return baseQuery;
  }, [isOcrReviewRequired, isSuperAdmin, type]);

  const serverSideQuery = useServerSideFilters({ query });

  return { query, serverSideQuery };
};

export const useExportedInitialQuery = ({ type }: InitialQueryProps) => {
  const query = useMemo(() => {
    const baseQuery = {
      ...(type && { type }),
      status: [InvoiceStatus.EXPORTED],
    };

    return baseQuery;
  }, [type]);

  const serverSideQuery = useServerSideFilters({ query });

  return { query, serverSideQuery };
};

export const useStandardInitialQuery = ({ type }: InitialQueryProps) => {
  const { isSuperAdmin } = usePermissions();
  const { isOcrReviewRequired } = useTenant();

  const query = useMemo(() => {
    const baseQuery = {
      ...(type && { type }),
      status: [InvoiceStatus.PREPROCESSED, InvoiceStatus.ERROR],
      isBooking: true,
      isDuplicate: false,
      reviewType: [InvoiceReviewType.STANDARD, InvoiceReviewType.ZERO_TOUCH],
      ...(isOcrReviewRequired &&
        !isSuperAdmin && {
          ocrReviewed: true,
        }),
    };

    return baseQuery;
  }, [isOcrReviewRequired, isSuperAdmin, type]);

  const serverSideQuery = useServerSideFilters({ query });

  return { query, serverSideQuery };
};

export const useFastlaneInitialFilters = () => {
  const initialColumnFilters = useMemo(() => {
    return [
      {
        id: "isBooking",
        value: true,
      },
      {
        id: "isDuplicate",
        value: false,
      },
      {
        id: "accountReviewed",
        value: false,
      },
      {
        id: "invoiceStatus",
        value: InvoiceStatus.PREPROCESSED,
      },
    ];
  }, []);

  return initialColumnFilters;
};

export const useFastlaneInitialQuery = ({ type }: InitialQueryProps) => {
  const { isSuperAdmin } = usePermissions();
  const { isOcrReviewRequired } = useTenant();

  const query = useMemo(() => {
    const baseQuery = {
      ...(type && { type }),
      isBooking: true,
      isDuplicate: false,
      reviewType: [InvoiceReviewType.FASTLANE],
      status: [InvoiceStatus.PREPROCESSED],
    };

    if (isOcrReviewRequired && !isSuperAdmin) {
      return {
        ...baseQuery,
        ocrReviewed: true,
      };
    }

    return baseQuery;
  }, [isOcrReviewRequired, isSuperAdmin, type]);

  const serverSideQuery = useServerSideFilters({ query });

  return { query, serverSideQuery };
};

export const useSortInitialQuery = ({ type }: InitialQueryProps) => {
  const { isSuperAdmin } = usePermissions();
  const { isOcrReviewRequired } = useTenant();

  const query = useMemo(() => {
    const baseQuery = {
      ...(type && { type }),
      status: [InvoiceStatus.PREPROCESSED, InvoiceStatus.ERROR],
      isBooking: false,
      isDuplicate: false,
    };

    if (isOcrReviewRequired && !isSuperAdmin) {
      return {
        ...baseQuery,
        ocrReviewed: true,
      };
    }

    return baseQuery;
  }, [isOcrReviewRequired, isSuperAdmin, type]);

  const serverSideQuery = useServerSideFilters({ query });

  return { query, serverSideQuery };
};

export const useDuplicatesInitialQuery = ({ type }: InitialQueryProps) => {
  const query = useMemo(() => {
    return {
      ...(type && { type }),
      status: [InvoiceStatus.PREPROCESSED],
      isDuplicate: true,
    };
  }, [type]);

  const serverSideQuery = useServerSideFilters({ query });

  return { query, serverSideQuery };
};

export const useUploadsInitialQuery = ({ type }: InitialQueryProps) => {
  const { isSuperAdmin } = usePermissions();
  const { isOcrReviewRequired } = useTenant();

  const query = useMemo(() => {
    if (isOcrReviewRequired && !isSuperAdmin) {
      return {
        ...(type && { type }),
        status: [
          InvoiceStatus.NEW,
          InvoiceStatus.PREPROCESSED,
          InvoiceStatus.ERROR,
        ],
        ocrReviewed: true,
      };
    } else {
      return {
        ...(type && { type }),
        status: [InvoiceStatus.ERROR, InvoiceStatus.NEW],
      };
    }
  }, [isOcrReviewRequired, isSuperAdmin, type]);

  const serverSideQuery = useServerSideFilters({ query });

  return { query, serverSideQuery };
};
